import { useEffect, useRef } from "react";
import { ArrowUpRight, FolderGit2, Lightbulb } from "lucide-react";
import { useIdea } from "@/components/SiteLayout";
import { prefersFlatMotion } from "@/lib/motion";

const LINES = [
  { text: "BUILDING", cls: "text-slate-50" },
  { text: "AI-FORWARD", cls: "text-cyan-400" },
  { text: "FULL-STACK", cls: "text-slate-50" },
  { text: "EXPERIENCES", cls: "text-outline" },
];

function ParticleField() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const isMobile = window.matchMedia("(hover: none)").matches || window.innerWidth < 768;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, isMobile ? 1.5 : 2);
    let w = 0, h = 0, raf;
    let visible = true;
    const mouse = { x: -10, y: -10 };
    const resize = () => {
      w = canvas.width = canvas.offsetWidth * dpr;
      h = canvas.height = canvas.offsetHeight * dpr;
    };
    resize();
    window.addEventListener("resize", resize);
    const N = isMobile ? 26 : 70;
    const pts = Array.from({ length: N }, () => ({
      x: Math.random(), y: Math.random(),
      vx: (Math.random() - 0.5) * 0.0005, vy: (Math.random() - 0.5) * 0.0005,
      r: Math.random() * 1.5 + 0.5,
    }));
    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = (e.clientX - rect.left) / rect.width;
      mouse.y = (e.clientY - rect.top) / rect.height;
    };
    if (!isMobile) window.addEventListener("mousemove", onMove);
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of pts) {
        p.x += p.vx; p.y += p.vy;
        const dx = mouse.x - p.x, dy = mouse.y - p.y;
        if (dx * dx + dy * dy < 0.03) { p.x -= dx * 0.003; p.y -= dy * 0.003; }
        if (p.x < 0 || p.x > 1) p.vx *= -1;
        if (p.y < 0 || p.y > 1) p.vy *= -1;
      }
      const linkDist = 130 * dpr;
      for (let i = 0; i < N; i++) {
        for (let j = i + 1; j < N; j++) {
          const a = pts[i], b = pts[j];
          const d = Math.hypot((a.x - b.x) * w, (a.y - b.y) * h);
          if (d < linkDist) {
            ctx.strokeStyle = `rgba(0,240,255,${0.1 * (1 - d / linkDist)})`;
            ctx.lineWidth = dpr * 0.7;
            ctx.beginPath();
            ctx.moveTo(a.x * w, a.y * h);
            ctx.lineTo(b.x * w, b.y * h);
            ctx.stroke();
          }
        }
      }
      for (const p of pts) {
        ctx.fillStyle = "rgba(0,240,255,0.65)";
        ctx.beginPath();
        ctx.arc(p.x * w, p.y * h, p.r * dpr, 0, Math.PI * 2);
        ctx.fill();
      }
    };
    const tick = () => {
      if (visible) draw();
      raf = requestAnimationFrame(tick);
    };
    if (reducedMotion || isMobile) {
      if (!isMobile) draw();
      return () => {
        window.removeEventListener("resize", resize);
        window.removeEventListener("mousemove", onMove);
      };
    }
    const obs = new IntersectionObserver(([e]) => { visible = e.isIntersecting; });
    obs.observe(canvas);
    tick();
    return () => {
      cancelAnimationFrame(raf);
      obs.disconnect();
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full opacity-70" />;
}

export default function Hero({ onOrder }) {
  const sectionRef = useRef(null);
  const openIdea = useIdea();
  const contentRef = useRef(null);
  const bgRef = useRef(null);

  // Scroll parallax without framer-motion (keeps it out of the main bundle). Skipped on touch / reduced motion.
  useEffect(() => {
    if (prefersFlatMotion()) return undefined;
    const section = sectionRef.current, content = contentRef.current, bg = bgRef.current;
    let raf = 0;
    const update = () => {
      raf = 0;
      const p = Math.min(Math.max(-section.getBoundingClientRect().top / (section.offsetHeight || 1), 0), 1);
      content.style.transform = `translateY(${(p * 220).toFixed(1)}px)`;
      content.style.opacity = Math.max(1 - p / 0.8, 0).toFixed(3);
      bg.style.transform = `translateY(${(p * 90).toFixed(1)}px)`;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const scrollToProjects = (e) => {
    const el = document.getElementById("projects");
    if (!el) return;
    e.preventDefault();
    el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={sectionRef} data-testid="hero-section" aria-labelledby="hero-headline-text" className="relative min-h-screen flex items-center overflow-hidden">
      <div ref={bgRef} className="anim-reveal absolute inset-0" aria-hidden="true">
        <ParticleField />
      </div>
      <div className="glow-orb glow-cyan-soft -top-64 right-[-20%] w-[820px] h-[820px]" aria-hidden="true" />
      <div className="glow-orb glow-violet-soft bottom-[-35%] left-[-25%] w-[700px] h-[700px]" aria-hidden="true" />

      <div ref={contentRef} className="anim-reveal relative z-10 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto w-full pt-28 pb-20">
        <div
          style={{ animationDelay: "0.15s" }}
          data-testid="hero-status-badge"
          className="anim-reveal hero-up inline-flex items-center gap-2.5 rounded-full border border-mint/30 bg-mint/5 px-4 py-1.5 mb-8"
        >
          <span className="w-2 h-2 rounded-full bg-mint pulse-dot" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-mint">Available for Q3/Q4 projects</span>
        </div>

        <h1 id="hero-headline-text" data-testid="hero-headline-text" className="font-display font-extrabold tracking-tight leading-[0.98] text-[clamp(1.5rem,7.4vw,3rem)] sm:text-7xl lg:text-[5.6rem]">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1 whitespace-nowrap">
              <span
                style={{ animationDelay: `${(0.35 + i * 0.13).toFixed(2)}s` }}
                className={`anim-reveal hero-line block ${line.cls}`}
              >
                {line.text}
              </span>
            </span>
          ))}
        </h1>

        <p
          style={{ animationDelay: "1.05s" }}
          className="anim-reveal hero-up mt-8 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
        >
          I design and ship web apps, SaaS platforms and AI integrations,
          from first pixel to deployed infrastructure. Fast, precise, no filler.
        </p>

        <div
          style={{ animationDelay: "1.12s" }}
          data-testid="hero-secure-by-design"
          className="anim-reveal hero-up mt-6 max-w-xl border-l-2 border-mint/60 pl-4"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-mint">Secure by design</p>
          <p className="mt-1.5 text-sm sm:text-base text-slate-400 leading-relaxed">
            AI should improve your business without turning your data into a liability.
          </p>
        </div>

        <div
          style={{ animationDelay: "1.2s" }}
          className="anim-reveal hero-up mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-order-now"
            onClick={onOrder}
            className="group flex items-center gap-2 rounded-full bg-cyan-400 text-[#030308] font-semibold px-7 py-3.5 hover:bg-mint transition-colors duration-300 glow-cyan"
          >
            Start A Project
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
          </button>
          <button
            data-testid="hero-cta-idea-generator"
            onClick={openIdea}
            className="group flex items-center gap-2 rounded-full border border-mint/40 bg-mint/5 text-mint font-semibold px-7 py-3.5 hover:bg-mint hover:text-[#030308] transition-colors duration-300"
          >
            <Lightbulb className="w-4 h-4" aria-hidden="true" />
            Idea Generator
          </button>
          <a
            data-testid="hero-cta-view-projects"
            href="/work"
            onClick={scrollToProjects}
            className="group flex items-center gap-2 rounded-full border border-white/15 text-slate-200 font-medium px-7 py-3.5 hover:border-cyan-400/50 hover:text-cyan-400 transition-colors duration-300"
          >
            <FolderGit2 className="w-4 h-4" aria-hidden="true" />
            View Previous Work
          </a>
        </div>

        <div
          style={{ animationDelay: "1.5s" }}
          className="anim-reveal hero-fade mt-16 flex flex-wrap gap-x-10 gap-y-3 font-mono text-xs uppercase tracking-[0.2em] text-slate-500"
        >
          <span>20+ apps shipped</span>
          <span className="text-cyan-400/60">/</span>
          <span>99.9% uptime record</span>
          <span className="text-cyan-400/60">/</span>
          <span>AI built in from day one</span>
        </div>
      </div>
    </section>
  );
}
